import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Sparkles, Shield, Users, HeartPulse,
  Clock, Compass, ArrowRight, Handshake, Lock, LineChart, Eye,
  Mic, MessageCircle, Sun, Moon, Activity, Wind, Feather, BookOpen,
  Fingerprint, Layers, Globe, Palette, CircleUser, Waves, Flower2,
  Timer, ShieldCheck, HeartHandshake, Music2, VolumeX as Volume2Off,
} from "lucide-react";
import Logo from "../components/Logo";

/* ────────────────────────────────────────────────
   Reveal on scroll
   ──────────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    el.querySelectorAll(".reveal").forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ────────────────────────────────────────────────
   Data
   ──────────────────────────────────────────────── */

const visionPoints = [
  {
    icon: HeartHandshake, tag: "Person, not diagnosis", title: "Care for the life around the treatment",
    body: "Cancer care is organized around clinical outcomes. People carry more than that: uncertainty, fatigue, family, work, identity, grief. My Mood Groove holds those private moments without diagnosing or replacing anyone.",
  },
  {
    icon: Users, tag: "For patients and caregivers", title: "The lived experience, not only the clinical file",
    body: "The platform is built for the person navigating cancer, and separately and privately for the people who love and support them. Two experiences. Two boundaries. One shared respect for what each is carrying.",
  },
  {
    icon: LineChart, tag: "Research-ready", title: "Evidence, not anecdotes",
    body: "Grounded in survivorship research: fear of recurrence, unmet psychosocial needs, cognitive and physical effects, identity, caregiver distress, and accessible digital support. Designed to generate consented, deidentified insight over time.",
  },
];

const supportWindows = [
  { icon: Compass, title: "During diagnosis", body: "A private space to absorb information, name fear, prepare questions, and steady oneself before the next decision." },
  { icon: Waves, title: "During treatment", body: "Support for uncertainty, fatigue, body changes, sleep, isolation, emotional overload, and the repeated rhythm of appointments." },
  { icon: Sun, title: "After treatment", body: "Awareness support for fear of recurrence, identity changes, lingering symptoms, return to work, relationships, and the expectation to feel normal again." },
  { icon: Users, title: "For families & caregivers", body: "Optional pathways for communication, boundaries, and caregiver strain, without exposing the person's private conversations." },
];

const journeySteps = [
  { n: "1", title: "Arrive", body: "The person arrives without predefined labels. Not strong, positive, afraid, or ill. Just present." },
  { n: "2", title: "Choose a purpose", body: "Calm down. Prepare for a scan. Process news. Sleep. Discuss body changes. Prepare a family conversation. Or simply be heard." },
  { n: "3", title: "Engage", body: "Voice, text, sound, breathing, grounding, quiet reflection, or a brief emotional check-in." },
  { n: "4", title: "Receive a response that matches the moment", body: "No diagnosis. No minimization. No overwhelm. No forced optimism." },
  { n: "5", title: "Identify the pressure", body: "Distinguish medical uncertainty from fatigue, family strain, appearance, identity, finances, grief, or fear of recurrence." },
  { n: "6", title: "Choose one realistic next step", body: "Rest. A question for the medical team. A trusted person. Patient navigator. Psycho-oncology. Or urgent care when needed." },
  { n: "7", title: "Return, over time", body: "See emotional patterns, changing needs, and personal strengths, without being reduced to a score." },
];

const doctrineGroups = [
  {
    label: "Agency & Personhood", accent: "from-[#EED89A]/60",
    items: [
      { n: 1, title: "Person centered, not cancer centered", body: "Begin with the person: their life, values, relationships, identity, culture, and goals. Cancer may be central in one moment and not in another." },
      { n: 2, title: "Preserve agency", body: "The person controls pace, topic, depth, memory, sound, biometric connection, and next action. The system does not force disclosure, gratitude, acceptance, or positivity." },
      { n: 3, title: "Protect body and identity complexity", body: "Treat appearance, sexuality, fertility, menopause, scarring, hair loss, weight change, disability, and intimacy with dignity, and without assumptions." },
      { n: 4, title: "Support informed conversation", body: "Help the person prepare questions for clinicians and patient navigators. Never interpret results, advise treatment, or replace informed consent." },
      { n: 5, title: "Respect survivorship diversity", body: "Do not treat survivorship as a finish line. Include active treatment, metastatic disease, recurrence, long term effects, remission, and uncertain futures." },
    ],
  },
  {
    label: "Emotional Processing & Validation", accent: "from-white/30",
    items: [
      { n: 6, title: "Validate uncertainty without predicting", body: "Acknowledge fear and ambiguity. Avoid false reassurance, prognosis, or claims about what a symptom means." },
      { n: 7, title: "Match emotional intensity", body: "When distress is high, use short, clear, grounding responses. Do not flood the person with questions, education, or complex reflection." },
      { n: 8, title: "Regulate before analyzing", body: "Support breathing, orientation, pacing, sensory grounding, and simple choices before deeper meaning-making." },
      { n: 9, title: "Do not pathologize understandable reactions", body: "Fear, anger, grief, numbness, envy, relief, exhaustion, ambivalence: recognize severity and duration without casually labeling disorder." },
      { n: 10, title: "Reject forced positivity", body: "Do not imply attitude determines outcome, or that the person must become inspirational to cope well." },
    ],
  },
  {
    label: "Practical Support & Boundaries", accent: "from-[#EED89A]/60",
    items: [
      { n: 11, title: "Distinguish the source of pressure", body: "Help separate medical uncertainty, treatment effects, sleep loss, pain, family strain, work, finances, identity, grief, and fear of recurrence." },
      { n: 12, title: "Strengthen connection without forcing disclosure", body: "Offer choices involving family, peers, support groups, psycho-oncology, spiritual care, and practical resources, while respecting privacy and readiness." },
      { n: 13, title: "Know the medical boundary", body: "The system does not diagnose, recommend treatment, interpret symptoms, advise medication, or delay urgent medical evaluation." },
      { n: 14, title: "Escalate responsibly", body: "When there are signs of imminent danger, severe impairment, abuse, or urgent medical need, move quickly toward designated human and emergency support." },
    ],
  },
  {
    label: "Privacy & Governance", accent: "from-white/30",
    items: [
      { n: 15, title: "Privacy before insight", body: "No donor, employer, clinician, family member, insurer, or partner organization receives private conversations or individual emotional scores without explicit consent." },
      { n: 16, title: "Patient-governed review", body: "Patients, survivors, people with metastatic disease, caregivers, psycho-oncology professionals, nurses, physicians, accessibility experts, and researchers review language, safety, bias, and outcomes." },
    ],
  },
];

const deepDives = [
  {
    icon: ShieldCheck, title: "Fear of recurrence, without false reassurance",
    may: ["Help name the fear", "Recognize triggers", "Reduce immediate activation", "Distinguish a thought from a medical fact", "Prepare questions", "Choose a support path"],
    mustNot: ["Say that recurrence is unlikely", "Interpret symptoms", "Promise that everything will be fine", "Discourage appropriate clinical evaluation"],
    coda: "The goal is not to eliminate uncertainty. It is to help the person remain more grounded and supported within it.",
  },
  {
    icon: Flower2, title: "The body after diagnosis and treatment",
    may: ["Use neutral, respectful language", "Allow grief for body changes without demanding acceptance", "Support agency in identifying what matters", "Help prepare a conversation about comfort, desire, and connection"],
    mustNot: ["Imply reconstruction, fertility, or appearance has one correct meaning", "Give medical or sexual health advice beyond appropriate referral", "Define identity through anatomy, appearance, or treatment choices"],
    coda: "The platform should never define a person through anatomy, appearance, or treatment choices.",
  },
  {
    icon: HeartHandshake, title: "Connection without new emotional obligation",
    may: ["Help prepare a conversation", "Identify what to share with others", "Set boundaries or ask for practical help", "Offer optional education for partners and family"],
    mustNot: ["Require the person to reassure relatives", "Ask them to perform optimism", "Disclose private sessions to family or caregivers", "Make them responsible for everyone else's coping"],
    coda: "Support should reduce isolation, not create a new emotional obligation.",
  },
];

const contrastRows = [
  ["Absorbs difficult information privately", "Diagnoses"],
  ["Names fear, prepares questions", "Interprets scans or symptoms"],
  ["Distinguishes a thought from a medical fact", "Predicts prognosis"],
  ["Regulates the moment", "Replaces psychotherapy"],
  ["Connects to relevant human resources", "Replaces clinicians"],
  ["Reflects patterns over time", "Reduces the person to a single score"],
];

const pilotPhases = [
  { n: "Phase 1", title: "Patient and expert review", body: "People across treatment and survivorship, metastatic patients, caregivers, psycho-oncology professionals, nurses, physicians, navigators, accessibility experts, and researchers review doctrine and scenarios." },
  { n: "Phase 2", title: "Prototype safety testing", body: "Test emotional fit, medical boundaries, usability, accessibility, response quality, escalation behavior, and trust." },
  { n: "Phase 3", title: "Controlled voluntary pilot", body: "A defined cohort uses the platform around appointments, treatment, sleep, fear of recurrence, family communication, body changes, and general awareness." },
  { n: "Phase 4", title: "Outcome evaluation", body: "Measure immediate distress change, awareness gained, communication readiness, support navigation, return use, privacy confidence, and adverse events." },
  { n: "Phase 5", title: "Protected program learning", body: "Evaluate only consented and deidentified aggregate patterns, to see whether support services can respond more effectively." },
  { n: "Phase 6", title: "Decision", body: "Refine, expand, pause, or discontinue based on patient feedback, clinical safety, accessibility, equity, and independent review." },
];

const partnerRoles = [
  { icon: Handshake, title: "Strategic convener", body: "Bring together patients, survivors, clinicians, researchers, designers, accessibility experts, and responsible AI specialists." },
  { icon: Sparkles, title: "Pilot catalyst", body: "Identify a cancer center or care pathway where the concept can be reviewed and tested responsibly." },
  { icon: Globe, title: "Global translation partner", body: "Help adapt the model across cultures and health systems while preserving common privacy and safety principles." },
  { icon: LineChart, title: "Research funder", body: "Support a rigorous person-centered study of emotional awareness, fear of recurrence, communication, navigation, and survivorship." },
  { icon: Users, title: "Leadership sponsor", body: "Fund researchers and clinical leaders, including those historically underrepresented, to shape the doctrine, evaluation, and future direction of the platform." },
  { icon: Compass, title: "Category shaper", body: "Help define what responsible, compassionate, AI-supported awareness for people facing cancer should be, and what it should not." },
];

const wearableStats = [
  { value: "31.8%", note: "of cancer survivors owned a wearable device", source: "National survivor survey, 2022" },
  { value: "82.2%", note: "of owners use their device most days or daily", source: "Cross-sectional survivor survey, 2026" },
  { value: "56.7%", note: "of cancer survivors owned a fitness tracker", source: "Cross-sectional survivor survey, 2026" },
];

const psychologyFrameworks = [
  {
    tag: "Clinical field",
    title: "Psycho-oncology",
    body: "The recognized clinical field studying psychological, social, behavioral, and ethical aspects of cancer. Every My Mood Groove doctrine sits inside this discipline, not outside it.",
    shapes: "Medical boundaries, escalation, distress calibration.",
  },
  {
    tag: "Cognitive care",
    title: "Cognitive Behavioral Therapy (CBT)",
    body: "Decades of evidence for reducing depression, anxiety, and fear of recurrence in survivors. Uses techniques like cognitive defusion, thought labeling, and structured question preparation.",
    shapes: "A thought is not a medical fact. Prepare a question for the medical team.",
  },
  {
    tag: "Values & flexibility",
    title: "Acceptance and Commitment Therapy (ACT)",
    body: "Psychological flexibility built on values, mindfulness, and defusion. Explicitly rejects forced positivity, and honors emotions that are hard to face.",
    shapes: "Reject forced positivity. Respect survivorship diversity.",
  },
  {
    tag: "Safety-first",
    title: "Trauma-informed care",
    body: "The SAMHSA principles of safety, trustworthiness, peer support, collaboration, empowerment, and cultural humility. Nothing in the app should retraumatize, coerce, or override consent.",
    shapes: "Preserve agency. Escalate responsibly. Never force disclosure.",
  },
  {
    tag: "Body-first regulation",
    title: "Polyvagal & somatic regulation",
    body: "Nervous-system-first approaches that support breathing, grounding, and pacing before deeper cognitive or emotional work. Aligned with the ambience library and voice check-in.",
    shapes: "Regulate before analyzing. Match emotional intensity.",
  },
  {
    tag: "Screening & conversation",
    title: "Motivational interviewing & distress screening",
    body: "Collaborative, non-directive conversation methods, plus validated distress thresholds like the NCCN Distress Thermometer, guide when to listen, when to reflect, and when to route to human support.",
    shapes: "Support informed conversation. Do not pathologize understandable reactions.",
  },
];

/* ────────────────────────────────────────────────
   Hero phone (large, auto-cycling across 4 screens)
   ──────────────────────────────────────────────── */

const HeroArrivingIcon = ({ name }) => {
  const stroke = "#EED89A";
  const s = 14;
  const paths = {
    droplet: <path d="M12 3s5 6 5 10a5 5 0 1 1-10 0c0-4 5-10 5-10z" />,
    cloud: <path d="M7 17h10a3.5 3.5 0 0 0 0-7 4.5 4.5 0 0 0-8.7-1.5A3.5 3.5 0 0 0 7 17z" />,
    question: <><path d="M9 9a3 3 0 1 1 4.5 2.6c-1 .6-1.5 1.2-1.5 2.4" /><circle cx="12" cy="17.5" r=".8" fill={stroke} stroke="none" /></>,
    sunrise: <><circle cx="12" cy="14" r="3.2" /><path d="M4 18h16M12 8v-2M6 10 4.5 8.5M18 10l1.5-1.5" /></>,
    knot: <path d="M8 8c2 3 6 3 8 0M8 12c2 3 6 3 8 0M8 16c2 3 6 3 8 0" />,
  };
  return (
    <svg viewBox="0 0 24 24" width={s * 1.7} height={s * 1.7} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

const heroScreens = [
  {
    id: "arriving",
    render: () => (
      <div className="hp-screen">
        <div className="hp-word">My Mood Groove</div>
        <div className="hp-h1">How are you<br />arriving today?</div>
        <div className="hp-ring">
          <div className="hp-ring-halo" />
          <div className="hp-ring-circle" />
          {[
            { name: "droplet", label: "Calm", angle: 270 },
            { name: "cloud", label: "Heavy", angle: 342 },
            { name: "question", label: "Uncertain", angle: 54 },
            { name: "sunrise", label: "Hopeful", angle: 126 },
            { name: "knot", label: "Overwhelmed", angle: 198 },
          ].map((m) => {
            const r = 78;
            const rad = (m.angle * Math.PI) / 180;
            const x = Math.cos(rad) * r;
            const y = Math.sin(rad) * r;
            return (
              <div key={m.label} className="hp-mood" style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}>
                <div className="hp-mood-btn"><HeroArrivingIcon name={m.name} /></div>
                <div className="hp-mood-lbl">{m.label}</div>
              </div>
            );
          })}
        </div>
        <div className="hp-supporting">You do not need to explain everything.<br />Start wherever feels comfortable.</div>
        <button className="hp-cta">Begin a private check-in</button>
        <div className="hp-subtle">Just breathe with me for a moment</div>
        <div className="hp-divider"><span className="hp-line" /><Sparkles size={9} className="text-[#EED89A]" /><span className="hp-line" /></div>
        <div className="hp-foot">Private by design. You control what is remembered.</div>
      </div>
    ),
  },
  {
    id: "purpose",
    render: () => (
      <div className="hp-screen">
        <div className="hp-word">My Mood Groove</div>
        <div className="hp-h1 small">What would help<br />right now?</div>
        <div className="hp-purpose-list">
          {["Prepare for a scan", "Process difficult news", "Steady before an appointment", "A family conversation", "Sleep is difficult", "Just be heard"].map((t, i) => (
            <div key={t} className={`hp-purpose-chip ${i === 2 ? "active" : ""}`}>{t}</div>
          ))}
        </div>
        <div className="hp-subtle">Choose one, several, or none</div>
        <button className="hp-cta">Continue</button>
        <div className="hp-foot">Nothing is transcribed unless you keep it.</div>
      </div>
    ),
  },
  {
    id: "voice",
    render: () => (
      <div className="hp-screen">
        <div className="hp-word">Voice check-in</div>
        <div className="hp-h1 small">You may speak,<br />or not.</div>
        <div className="hp-mic-area">
          <div className="hp-mic-ring"><div className="hp-mic-core"><Mic size={22} /></div></div>
          <div className="hp-mic-wave"><span/><span/><span/><span/><span/></div>
        </div>
        <div className="hp-row-lbl">Ambience library <span>Browse ›</span></div>
        <div className="hp-amb-grid">
          <div className="hp-amb active"><Music2 size={11} /> Warm piano</div>
          <div className="hp-amb"><Waves size={11} /> Ocean</div>
          <div className="hp-amb"><Wind size={11} /> Rain</div>
          <div className="hp-amb"><Volume2Off size={11} /> Silence</div>
        </div>
        <div className="hp-row-lbl">Visual landscape <span>Cast ›</span></div>
        <div className="hp-vis-grid">
          <div className="hp-vis active"><span className="hp-swatch aurora" /> Aurora</div>
          <div className="hp-vis"><span className="hp-swatch forest" /> Forest</div>
          <div className="hp-vis"><span className="hp-swatch tide" /> Tide</div>
        </div>
        <div className="hp-cast"><span className="hp-cast-dot" /> Playing to <b>Living-room TV</b></div>
      </div>
    ),
  },
  {
    id: "wearable",
    render: () => (
      <div className="hp-screen">
        <div className="hp-word">Body-mind bridge</div>
        <div className="hp-h1 small">Your sleep was thin.<br />The day felt long.</div>
        <div className="hp-wear-grid">
          <div className="hp-wear"><Moon size={14} className="text-[#EED89A]" /><div><div className="hp-wv">5h 42m</div><div className="hp-wl">Sleep</div></div></div>
          <div className="hp-wear"><Activity size={14} className="text-[#EED89A]" /><div><div className="hp-wv">Low</div><div className="hp-wl">Recovery</div></div></div>
          <div className="hp-wear"><HeartPulse size={14} className="text-[#EED89A]" /><div><div className="hp-wv">62 bpm</div><div className="hp-wl">Resting</div></div></div>
          <div className="hp-wear"><Wind size={14} className="text-[#EED89A]" /><div><div className="hp-wv">14/min</div><div className="hp-wl">Breath</div></div></div>
        </div>
        <div className="hp-supporting">These are not diagnoses.<br />They may help you notice, and choose.</div>
        <button className="hp-cta">A gentle check-in</button>
        <div className="hp-foot">Optional signal. Private meaning.</div>
      </div>
    ),
  },
];

function HeroPhone() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % heroScreens.length), 5200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="hero-phone-wrap" data-testid="lwc-hero-phone">
      <div className="hero-phone-halo" aria-hidden />
      <div className="hero-phone-frame">
        <div className="hero-phone-notch" />
        <div className="hero-phone-body">
          {heroScreens.map((s, i) => (
            <div key={s.id} className={`hero-phone-screen ${i === idx ? "active" : ""}`} data-testid={`lwc-hero-screen-${s.id}`}>
              {s.render()}
            </div>
          ))}
        </div>
      </div>
      <div className="hero-phone-dots">
        {heroScreens.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setIdx(i)}
            className={`hero-phone-dot ${i === idx ? "active" : ""}`}
            aria-label={`Show ${s.id} screen`}
            data-testid={`lwc-hero-dot-${s.id}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────
   Phone mockup component (6 screens)
   ──────────────────────────────────────────────── */

function PhoneFrame({ children, testid, floatVariant = "a" }) {
  return (
    <div className={`phone-shell ${floatVariant === "b" ? "phone-float-b" : "phone-float-a"}`} data-testid={testid}>
      <div className="phone-notch" />
      <div className="phone-screen">{children}</div>
    </div>
  );
}

const phoneScreens = [
  {
    id: "arrive",
    label: "Arrive",
    body: (
      <>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#EED89A]/90 mb-4">A private moment</div>
        <div className="text-white font-heading text-lg leading-snug mb-3">You don't have to be anything right now.</div>
        <div className="text-white/70 text-[13px] leading-relaxed mb-6">Not strong. Not positive. Not afraid. Just here.</div>
        <div className="mt-auto space-y-2">
          <div className="mmg-btn">Begin quietly</div>
          <div className="mmg-btn ghost">Learn what this is</div>
        </div>
      </>
    ),
  },
  {
    id: "purpose",
    label: "Choose a purpose",
    body: (
      <>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#EED89A]/90 mb-3">What would help right now?</div>
        <div className="text-white font-heading text-base leading-snug mb-4">Choose one, or none.</div>
        <div className="space-y-2">
          {["Prepare for a scan","Process difficult news","Steady before an appointment","A family conversation","Sleep is difficult","Just be heard"].map((t) => (
            <div key={t} className="mmg-chip">{t}</div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "voice",
    label: "Voice check-in",
    body: (
      <>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#EED89A]/90 mb-3">You may speak, or not.</div>
        <div className="flex flex-col items-center gap-3 mb-3">
          <div className="mic-ring">
            <div className="mic-core"><Mic size={20} /></div>
          </div>
          <div className="mic-wave"><span/><span/><span/><span/><span/></div>
        </div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[9px] uppercase tracking-[0.22em] text-white/55">Ambience library</div>
          <div className="text-[8px] uppercase tracking-[0.18em] text-[#EED89A]/85">Browse ›</div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 mb-2">
          <div className="amb-chip active"><Music2 size={11} /><span>Warm piano</span></div>
          <div className="amb-chip"><Waves size={11} /><span>Ocean</span></div>
          <div className="amb-chip"><Wind size={11} /><span>Rain</span></div>
          <div className="amb-chip"><Volume2Off size={11} /><span>Silence</span></div>
        </div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[9px] uppercase tracking-[0.22em] text-white/55">Visual landscape</div>
          <div className="text-[8px] uppercase tracking-[0.18em] text-[#EED89A]/85">Cast ›</div>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-2">
          <div className="vis-chip active"><span className="vis-swatch aurora" /><span>Aurora</span></div>
          <div className="vis-chip"><span className="vis-swatch forest" /><span>Forest</span></div>
          <div className="vis-chip"><span className="vis-swatch tide" /><span>Tide</span></div>
        </div>
        <div className="cast-row">
          <div className="cast-dot" /> Playing to <span className="cast-target">Living-room TV</span>
        </div>
      </>
    ),
  },
  {
    id: "fear",
    label: "Fear of recurrence",
    body: (
      <>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#EED89A]/90 mb-3">Grounding, not prediction</div>
        <div className="text-white font-heading text-[15px] leading-snug mb-4">A thought is not the same as a medical fact.</div>
        <div className="fear-card">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/60 mb-1">Thought</div>
          <div className="text-white text-[13px] leading-relaxed">This pain must mean it's back.</div>
        </div>
        <div className="fear-card gold">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A]/90 mb-1">What is known</div>
          <div className="text-white text-[13px] leading-relaxed">It is a fear. It is not a scan.</div>
        </div>
        <div className="mt-auto text-white/60 text-[11px] leading-relaxed">
          You may prepare a question for your medical team.
        </div>
      </>
    ),
  },
  {
    id: "next",
    label: "One next step",
    body: (
      <>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#EED89A]/90 mb-3">A single realistic choice</div>
        <div className="text-white font-heading text-[15px] leading-snug mb-4">What feels honest right now?</div>
        <div className="space-y-2">
          <div className="mmg-step">Rest for twenty minutes<span className="mmg-step-hint">Body</span></div>
          <div className="mmg-step">A question for the medical team<span className="mmg-step-hint">Prepare</span></div>
          <div className="mmg-step">Message a trusted person<span className="mmg-step-hint">Connection</span></div>
          <div className="mmg-step urgent">Urgent care<span className="mmg-step-hint">Available</span></div>
        </div>
      </>
    ),
  },
  {
    id: "wearable",
    label: "Body–mind bridge",
    body: (
      <>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#EED89A]/90 mb-3">Optional signal, private meaning</div>
        <div className="text-white font-heading text-[14px] leading-snug mb-3">Your sleep was thin. You said the day felt long.</div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="wear-card"><Moon size={14} className="text-[#EED89A]" /><div><div className="wear-v">5h 42m</div><div className="wear-l">Sleep</div></div></div>
          <div className="wear-card"><Activity size={14} className="text-[#EED89A]" /><div><div className="wear-v">Low</div><div className="wear-l">Recovery</div></div></div>
          <div className="wear-card"><HeartPulse size={14} className="text-[#EED89A]" /><div><div className="wear-v">62 bpm</div><div className="wear-l">Resting</div></div></div>
          <div className="wear-card"><Wind size={14} className="text-[#EED89A]" /><div><div className="wear-v">14/min</div><div className="wear-l">Breath</div></div></div>
        </div>
        <div className="text-white/60 text-[11px] leading-relaxed">
          These are not diagnoses. They may help you notice, and choose.
        </div>
      </>
    ),
  },
];

/* ────────────────────────────────────────────────
   Page
   ──────────────────────────────────────────────── */

export default function LivingWithCancerPage() {
  const scrollRef = useReveal();
  const [activeScreen, setActiveScreen] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);

  // Auto-cycle phone screens gently, stops the moment the user picks a screen.
  useEffect(() => {
    if (userInteracted) return;
    const id = setInterval(() => setActiveScreen((s) => (s + 1) % phoneScreens.length), 5200);
    return () => clearInterval(id);
  }, [userInteracted]);

  const selectScreen = (i) => {
    setUserInteracted(true);
    setActiveScreen(i);
  };

  // Auth guard (mirror of AnaPage pattern)
  if (typeof window !== "undefined" && sessionStorage.getItem("mmg_lwc_access") !== "granted") {
    return <Navigate to="/living-with-cancer-access" replace />;
  }

  return (
    <div ref={scrollRef} className="lwc-scope min-h-screen relative text-white overflow-x-hidden">
      <style>{`
        .lwc-scope {
          background:
            radial-gradient(1200px 700px at 15% -10%, rgba(90,130,210,0.38), transparent 60%),
            radial-gradient(900px 600px at 90% 8%, rgba(238,216,154,0.22), transparent 55%),
            radial-gradient(700px 500px at 8% 45%, rgba(246,208,140,0.14), transparent 65%),
            radial-gradient(900px 600px at 95% 62%, rgba(238,216,154,0.18), transparent 60%),
            radial-gradient(1000px 700px at 50% 100%, rgba(30,55,120,0.45), transparent 60%),
            linear-gradient(180deg, #0F1E44 0%, #1A3673 45%, #0E1D42 100%);
        }
        .lwc-scope .paper-band {
          background:
            radial-gradient(circle at 15% 30%, rgba(255,252,240,0.04) 0, transparent 45%),
            radial-gradient(circle at 82% 70%, rgba(238,216,154,0.14) 0, transparent 55%),
            radial-gradient(circle at 30% 90%, rgba(246,228,178,0.10) 0, transparent 55%),
            linear-gradient(180deg, rgba(244,240,230,0.04), rgba(244,240,230,0.07));
        }
        .lwc-scope .ivory-band {
          background: linear-gradient(180deg, #EAF1F7 0%, #DCE7F1 100%);
          color: #132A5C;
        }
        .reveal { opacity: 0; transform: translateY(24px); transition: opacity .9s cubic-bezier(.2,.9,.3,1), transform .9s cubic-bezier(.2,.9,.3,1); }
        .reveal.is-visible { opacity: 1; transform: translateY(0); }
        .lwc-heading {
          background-image: linear-gradient(100deg, #ffffff 42%, rgba(255,255,255,0.6) 50%, #ffffff 58%);
          background-size: 260% 100%; background-position: 100% 0;
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
          position: relative; padding-bottom: 18px;
        }
        .reveal.is-visible .lwc-heading { animation: lwcShimmer 1.7s cubic-bezier(0.22,1,0.36,1) .15s 1 forwards; }
        @keyframes lwcShimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }
        .lwc-heading::after {
          content:""; position:absolute; left:0; bottom:0;
          height:1px; width:180px; max-width:40%;
          background: linear-gradient(90deg, rgba(238,216,154,0.9), rgba(238,216,154,0.15) 70%, transparent);
          transform: scaleX(0); transform-origin: left center;
          transition: transform 1.2s cubic-bezier(0.65,0,0.35,1) 0.45s;
        }
        .lwc-heading.centered::after {
          left: 50%; transform: translateX(-50%) scaleX(0); transform-origin: center;
          background: linear-gradient(90deg, transparent 0%, rgba(238,216,154,0.9) 50%, transparent 100%);
        }
        .reveal.is-visible .lwc-heading::after { transform: scaleX(1); }
        .reveal.is-visible .lwc-heading.centered::after { transform: translateX(-50%) scaleX(1); }
        .lwc-heading.ivory { background: none; -webkit-text-fill-color: initial; color: #132A5C; }
        .lwc-heading.ivory::after { background: linear-gradient(90deg, rgba(19,42,92,0.7), rgba(19,42,92,0.1) 70%, transparent); }

        .glass-card {
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(238,216,154,0.28);
          backdrop-filter: blur(14px);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.06),
            inset 0 0 0 1px rgba(238,216,154,0.05),
            0 24px 60px rgba(10,20,40,0.35),
            0 0 40px -12px rgba(238,216,154,0.10);
        }
        .glass-soft {
          background: rgba(255,255,255,0.028);
          border: 1px solid rgba(255,255,255,0.10);
          backdrop-filter: blur(10px);
        }
        .gold-divider { height:1px; width:100%; background: linear-gradient(90deg, transparent, rgba(238,216,154,0.6), transparent); }

        /* Floating gold motes */
        .lwc-mote {
          position: absolute; width: 4px; height: 4px; border-radius: 50%;
          background: #F6E4B2;
          box-shadow: 0 0 14px 3px rgba(246,228,178,0.9), 0 0 34px 8px rgba(238,216,154,0.45);
          animation: motesRise linear infinite;
          pointer-events: none;
        }
        .lwc-mote.large {
          width: 6px; height: 6px;
          box-shadow: 0 0 18px 4px rgba(246,228,178,1), 0 0 42px 12px rgba(238,216,154,0.55);
        }
        @keyframes motesRise {
          0%   { transform: translate3d(0, 20vh, 0) scale(.6); opacity: 0; }
          10%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate3d(20px, -110vh, 0) scale(1.05); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) { .lwc-mote { display: none; } }

        /* Phone mockup */
        .phone-shell {
          position: relative; width: 260px; height: 540px;
          background: linear-gradient(180deg, #0d1830 0%, #142545 100%);
          border-radius: 40px; padding: 12px;
          border: 1px solid rgba(238,216,154,0.28);
          box-shadow:
            0 40px 90px rgba(6,14,32,0.6),
            inset 0 1px 0 rgba(255,255,255,0.08),
            0 0 0 3px rgba(0,0,0,0.35);
        }
        .phone-float-a { animation: phoneFloatA 8s ease-in-out infinite; }
        .phone-float-b { animation: phoneFloatB 9.5s ease-in-out infinite; }
        @keyframes phoneFloatA { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes phoneFloatB { 0%,100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
        @media (prefers-reduced-motion: reduce) { .phone-float-a, .phone-float-b { animation: none; } }
        .phone-notch {
          position: absolute; top: 8px; left: 50%; transform: translateX(-50%);
          width: 88px; height: 20px; border-radius: 12px; background: #06101f;
          border: 1px solid rgba(0,0,0,0.6);
        }
        .phone-screen {
          position: relative;
          width: 100%; height: 100%; border-radius: 30px;
          background:
            radial-gradient(circle at 30% 20%, rgba(115,150,205,0.35), transparent 60%),
            radial-gradient(circle at 70% 80%, rgba(238,216,154,0.15), transparent 60%),
            linear-gradient(180deg, #223a63 0%, #1a2d4f 100%);
          padding: 44px 18px 20px; display: flex; flex-direction: column;
          overflow: hidden;
        }
        .phone-screen::before {
          content:""; position:absolute; inset:0;
          background:
            radial-gradient(1.2px 1.2px at 22% 30%, rgba(255,255,255,0.7), transparent 55%),
            radial-gradient(1.4px 1.4px at 78% 22%, rgba(246,228,178,0.7), transparent 55%),
            radial-gradient(1.1px 1.1px at 62% 78%, rgba(255,255,255,0.6), transparent 55%);
          animation: phoneStars 4s ease-in-out infinite alternate;
          pointer-events: none;
        }
        @keyframes phoneStars { from { opacity: .4; } to { opacity: .9; } }

        .mmg-btn {
          background: linear-gradient(180deg, #F4EAC8, #E6D19E);
          color: #22314E; text-align:center; padding: 10px; border-radius: 12px;
          font-family: var(--font-heading, serif); font-weight: 500; font-size: 13px;
          box-shadow: 0 6px 18px rgba(238,216,154,0.28);
        }
        .mmg-btn.ghost {
          background: transparent; color: rgba(255,255,255,0.85); border: 1px solid rgba(255,255,255,0.18);
          box-shadow: none;
        }
        .mmg-chip {
          background: rgba(255,255,255,0.06); border: 1px solid rgba(238,216,154,0.25);
          border-radius: 10px; padding: 8px 10px; color: #fff; font-size: 12px;
        }
        .mmg-step {
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
          border-radius: 12px; padding: 10px 12px; color: #fff; font-size: 12px;
          display: flex; justify-content: space-between; align-items: center;
        }
        .mmg-step.urgent { border-color: rgba(246,228,178,0.55); background: rgba(238,216,154,0.10); }
        .mmg-step-hint { font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase; color: rgba(238,216,154,0.85); }

        .mic-ring {
          width: 108px; height: 108px; border-radius: 50%;
          background: radial-gradient(circle, rgba(238,216,154,0.16), transparent 70%);
          border: 1px solid rgba(238,216,154,0.35);
          display: flex; align-items: center; justify-content: center;
          animation: micPulse 2.6s ease-in-out infinite;
        }
        .mic-core {
          width: 62px; height: 62px; border-radius: 50%;
          background: linear-gradient(180deg, #F4EAC8, #E6D19E);
          color: #22314E; display:flex; align-items:center; justify-content:center;
          box-shadow: 0 10px 26px rgba(238,216,154,0.35);
        }
        @keyframes micPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(238,216,154,0.25); }
          50% { box-shadow: 0 0 0 18px rgba(238,216,154,0); }
        }

        /* Cast/projection status row on Voice screen */
        .cast-row {
          display:flex; align-items:center; gap:6px;
          background: rgba(238,216,154,0.10); border: 1px solid rgba(238,216,154,0.35);
          border-radius: 10px; padding: 6px 8px;
          color: rgba(255,255,255,0.85); font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase;
          margin-top: auto;
        }
        .cast-dot {
          width: 6px; height: 6px; border-radius: 50%; background: #7CE0A3;
          box-shadow: 0 0 8px 2px rgba(124,224,163,0.6); animation: castPulse 2s ease-in-out infinite;
        }
        .cast-target { color: #EED89A; letter-spacing: 0.08em; }
        @keyframes castPulse { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .cast-dot { animation: none; } }

        .fear-card {
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.10);
          border-radius: 12px; padding: 10px 12px; margin-bottom: 8px;
        }
        .fear-card.gold { border-color: rgba(238,216,154,0.5); background: rgba(238,216,154,0.09); }

        /* Ambience + visual chips on Voice screen */
        .amb-chip, .vis-chip {
          display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2px;
          background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10);
          border-radius: 9px; padding: 6px 4px; color: rgba(255,255,255,0.7);
          font-size: 8px; letter-spacing: 0.06em; text-transform: uppercase; line-height:1.1;
        }
        .amb-chip svg, .vis-chip svg { color: rgba(255,255,255,0.55); }
        .amb-chip.active, .vis-chip.active {
          background: rgba(238,216,154,0.16); border-color: rgba(238,216,154,0.55); color: #fff;
        }
        .amb-chip.active svg, .vis-chip.active svg { color: #EED89A; }
        .vis-swatch { width: 22px; height: 10px; border-radius: 999px; margin-bottom: 2px; }
        .vis-swatch.aurora { background: linear-gradient(90deg, #6ea3ff, #c5b0ff 50%, #EED89A); }
        .vis-swatch.forest { background: linear-gradient(90deg, #1f4d3a, #7fa987 60%, #EED89A); }
        .vis-swatch.tide { background: linear-gradient(90deg, #0d3d63, #4b8fbd 60%, #cfe8f6); }
        .mic-wave { display:flex; gap:3px; align-items:flex-end; height:14px; }
        .mic-wave span {
          display:block; width:3px; background:#EED89A; border-radius:2px;
          animation: micBars 1.2s ease-in-out infinite;
        }
        .mic-wave span:nth-child(1){ height:40%; animation-delay:0s; }
        .mic-wave span:nth-child(2){ height:70%; animation-delay:.15s; }
        .mic-wave span:nth-child(3){ height:100%; animation-delay:.3s; }
        .mic-wave span:nth-child(4){ height:65%; animation-delay:.45s; }
        .mic-wave span:nth-child(5){ height:35%; animation-delay:.6s; }
        @keyframes micBars { 0%,100% { transform: scaleY(0.6); opacity:.6; } 50% { transform: scaleY(1.15); opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .mic-wave span { animation:none; } }

        /* Wearable illustration float, no rotation, gentle vertical only */
        .lwc-wear { filter: drop-shadow(0 20px 40px rgba(4,12,40,0.5)); }
        .lwc-wear-a { animation: lwcWearA 8s ease-in-out infinite; }
        .lwc-wear-b { animation: lwcWearB 9.5s ease-in-out infinite; }
        @keyframes lwcWearA { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes lwcWearB { 0%,100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
        @media (prefers-reduced-motion: reduce) { .lwc-wear-a, .lwc-wear-b { animation:none; } }

        .wear-card {
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
          border-radius: 10px; padding: 8px 10px; display:flex; gap:8px; align-items:center;
        }
        .wear-v { color: #fff; font-size: 12px; font-weight: 500; line-height: 1; }
        .wear-l { color: rgba(255,255,255,0.6); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; margin-top: 3px; }

        .screen-tab {
          font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase;
          color: rgba(255,255,255,0.55); padding: 8px 14px; border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.10); cursor: pointer;
          transition: all .3s ease;
        }
        .screen-tab.active {
          color: #22314E; background: linear-gradient(180deg, #F4EAC8, #E6D19E);
          border-color: transparent;
          box-shadow: 0 4px 14px rgba(238,216,154,0.35);
        }
        .screen-tab:hover:not(.active) { color: #fff; border-color: rgba(238,216,154,0.35); }
        /* Hero phone (large, animated, HTML/CSS built — fonts match site exactly) */
        .hero-phone-wrap {
          position: relative;
          width: 300px;
          max-width: 300px;
          margin: 0 auto;
          padding: 8px 0;
          animation: heroPhoneFloat 9s ease-in-out infinite;
        }
        @media (min-width: 1024px) {
          .hero-phone-wrap { width: 320px; max-width: 320px; }
        }
        @keyframes heroPhoneFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @media (prefers-reduced-motion: reduce) { .hero-phone-wrap { animation: none; } }

        .hero-phone-halo {
          position: absolute; inset: -8% -14% -6% -14%;
          background: radial-gradient(circle at 50% 50%, rgba(238,216,154,0.30) 0%, rgba(238,216,154,0.12) 32%, transparent 68%);
          filter: blur(30px); z-index: 0; pointer-events: none;
          animation: heroHalo 8s ease-in-out infinite;
        }
        @keyframes heroHalo { 0%,100% { opacity: .85; transform: scale(1); } 50% { opacity: 1; transform: scale(1.04); } }

        .hero-phone-frame {
          position: relative; z-index: 1;
          aspect-ratio: 9 / 19;
          background: linear-gradient(180deg, #1a1c22 0%, #101216 100%);
          border-radius: 44px; padding: 10px;
          border: 1.5px solid rgba(238,216,154,0.30);
          box-shadow:
            0 55px 100px rgba(4,10,30,0.55),
            0 0 0 2px rgba(0,0,0,0.4),
            inset 0 1px 0 rgba(255,255,255,0.08),
            0 0 80px -10px rgba(238,216,154,0.15);
        }
        .hero-phone-notch {
          position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
          width: 96px; height: 22px; border-radius: 14px; background: #05070c;
          border: 1px solid rgba(0,0,0,0.6); z-index: 3;
        }
        .hero-phone-body {
          position: relative; width: 100%; height: 100%;
          border-radius: 34px; overflow: hidden;
          background:
            radial-gradient(circle at 30% 15%, rgba(90,130,205,0.25), transparent 55%),
            radial-gradient(circle at 75% 85%, rgba(238,216,154,0.14), transparent 60%),
            linear-gradient(180deg, #17305A 0%, #0E1D42 100%);
        }
        .hero-phone-screen {
          position: absolute; inset: 0;
          padding: 46px 22px 20px;
          display: flex; flex-direction: column; align-items: center;
          opacity: 0; transition: opacity .9s ease;
          pointer-events: none;
        }
        .hero-phone-screen.active { opacity: 1; pointer-events: auto; }

        .hp-screen { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; }
        .hp-word {
          color: #EED89A; font-family: var(--font-heading);
          font-weight: 500; font-size: 12px; letter-spacing: 0.02em;
          margin-bottom: 10px; text-align: center;
        }
        .hp-h1 {
          color: #ffffff; font-family: var(--font-heading);
          font-weight: 500; font-size: 22px; line-height: 1.15;
          letter-spacing: -0.01em; text-align: center; margin-bottom: 18px;
        }
        .hp-h1.small { font-size: 19px; }
        .hp-supporting {
          color: rgba(255,255,255,0.78); font-family: var(--font-body);
          font-size: 11.5px; line-height: 1.45; text-align: center; margin: 10px 0 14px;
        }
        .hp-cta {
          background: linear-gradient(180deg, #F4EAC8, #E6D19E);
          color: #0F1E44; font-family: var(--font-heading);
          font-weight: 500; font-size: 13px; padding: 11px 22px;
          border-radius: 999px; border: none;
          box-shadow: 0 10px 24px rgba(238,216,154,0.35), 0 0 0 1px rgba(238,216,154,0.5) inset;
          cursor: pointer;
        }
        .hp-subtle {
          color: rgba(255,255,255,0.7); font-family: var(--font-body);
          font-size: 10.5px; line-height: 1.4; margin-top: 8px; text-align: center;
        }
        .hp-divider {
          display: flex; align-items: center; gap: 8px; margin: 12px 0 8px; width: 60%;
        }
        .hp-divider .hp-line {
          flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(238,216,154,0.5), transparent);
        }
        .hp-foot {
          color: rgba(255,255,255,0.5); font-family: var(--font-body);
          font-size: 9px; text-align: center; margin-top: auto; padding-top: 6px;
        }

        /* Arriving ring */
        .hp-ring {
          position: relative; width: 200px; height: 200px; margin: 8px auto 6px;
        }
        .hp-ring-halo {
          position: absolute; inset: 12px;
          border-radius: 50%;
          box-shadow: 0 0 40px 6px rgba(238,216,154,0.18), inset 0 0 30px rgba(238,216,154,0.10);
        }
        .hp-ring-circle {
          position: absolute; inset: 30px;
          border-radius: 50%;
          border: 1px solid rgba(238,216,154,0.75);
          box-shadow: 0 0 24px rgba(238,216,154,0.25);
        }
        .hp-mood {
          position: absolute; top: 50%; left: 50%;
          display: flex; flex-direction: column; align-items: center; gap: 4px;
        }
        .hp-mood-btn {
          width: 34px; height: 34px; border-radius: 50%;
          background: #0F1E44; border: 1px solid rgba(238,216,154,0.7);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.35);
        }
        .hp-mood-lbl {
          color: #ffffff; font-family: var(--font-body);
          font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase;
          text-shadow: 0 2px 6px rgba(0,0,0,0.5);
        }

        /* Purpose chips */
        .hp-purpose-list { display: flex; flex-direction: column; gap: 7px; width: 100%; margin: 6px 0 10px; }
        .hp-purpose-chip {
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
          border-radius: 10px; padding: 9px 12px;
          color: #ffffff; font-family: var(--font-body); font-size: 11.5px; text-align: center;
        }
        .hp-purpose-chip.active {
          background: rgba(238,216,154,0.12); border-color: rgba(238,216,154,0.55); color: #ffffff;
          box-shadow: 0 0 18px rgba(238,216,154,0.15);
        }

        /* Voice */
        .hp-mic-area { display: flex; flex-direction: column; align-items: center; gap: 8px; margin: 6px 0 10px; }
        .hp-mic-ring {
          width: 84px; height: 84px; border-radius: 50%;
          background: radial-gradient(circle, rgba(238,216,154,0.16), transparent 70%);
          border: 1px solid rgba(238,216,154,0.4);
          display: flex; align-items: center; justify-content: center;
          animation: hpMicPulse 2.6s ease-in-out infinite;
        }
        .hp-mic-core {
          width: 50px; height: 50px; border-radius: 50%;
          background: linear-gradient(180deg, #F4EAC8, #E6D19E);
          color: #0F1E44; display: flex; align-items: center; justify-content: center;
          box-shadow: 0 8px 20px rgba(238,216,154,0.35);
        }
        @keyframes hpMicPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(238,216,154,0.25); } 50% { box-shadow: 0 0 0 14px rgba(238,216,154,0); } }
        .hp-mic-wave { display: flex; gap: 3px; align-items: flex-end; height: 12px; }
        .hp-mic-wave span {
          display: block; width: 3px; background: #EED89A; border-radius: 2px;
          animation: hpBars 1.2s ease-in-out infinite;
        }
        .hp-mic-wave span:nth-child(1) { height: 40%; animation-delay: 0s; }
        .hp-mic-wave span:nth-child(2) { height: 70%; animation-delay: .15s; }
        .hp-mic-wave span:nth-child(3) { height: 100%; animation-delay: .3s; }
        .hp-mic-wave span:nth-child(4) { height: 65%; animation-delay: .45s; }
        .hp-mic-wave span:nth-child(5) { height: 35%; animation-delay: .6s; }
        @keyframes hpBars { 0%,100% { transform: scaleY(0.6); opacity: .6; } 50% { transform: scaleY(1.15); opacity: 1; } }
        .hp-row-lbl {
          display: flex; justify-content: space-between; align-items: center;
          width: 100%; color: rgba(255,255,255,0.55); font-family: var(--font-body);
          font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase;
          margin-bottom: 5px; margin-top: 4px;
        }
        .hp-row-lbl span { color: #EED89A; letter-spacing: 0.12em; }
        .hp-amb-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; width: 100%; margin-bottom: 4px; }
        .hp-vis-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px; width: 100%; margin-bottom: 6px; }
        .hp-amb, .hp-vis {
          display: flex; align-items: center; justify-content: center; gap: 4px;
          background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10);
          border-radius: 8px; padding: 7px 4px;
          color: rgba(255,255,255,0.7); font-family: var(--font-body);
          font-size: 8.5px; letter-spacing: 0.04em; text-transform: uppercase;
        }
        .hp-amb.active, .hp-vis.active {
          background: rgba(238,216,154,0.16); border-color: rgba(238,216,154,0.55); color: #ffffff;
        }
        .hp-amb.active svg, .hp-vis.active svg { color: #EED89A; }
        .hp-swatch { width: 16px; height: 8px; border-radius: 999px; }
        .hp-swatch.aurora { background: linear-gradient(90deg, #6ea3ff, #c5b0ff 50%, #EED89A); }
        .hp-swatch.forest { background: linear-gradient(90deg, #1f4d3a, #7fa987 60%, #EED89A); }
        .hp-swatch.tide { background: linear-gradient(90deg, #0d3d63, #4b8fbd 60%, #cfe8f6); }
        .hp-cast {
          margin-top: auto; display: flex; align-items: center; gap: 6px;
          background: rgba(238,216,154,0.10); border: 1px solid rgba(238,216,154,0.35);
          border-radius: 10px; padding: 6px 8px; width: 100%;
          color: rgba(255,255,255,0.85); font-family: var(--font-body);
          font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase;
        }
        .hp-cast b { color: #EED89A; font-weight: 500; letter-spacing: 0.08em; }
        .hp-cast-dot {
          width: 6px; height: 6px; border-radius: 50%; background: #7CE0A3;
          box-shadow: 0 0 8px 2px rgba(124,224,163,0.6); animation: hpCastPulse 2s ease-in-out infinite;
        }
        @keyframes hpCastPulse { 0%,100% { opacity: .7; } 50% { opacity: 1; } }

        /* Wearable grid */
        .hp-wear-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; width: 100%; margin: 6px 0 8px; }
        .hp-wear {
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
          border-radius: 10px; padding: 8px 10px; display: flex; gap: 8px; align-items: center;
        }
        .hp-wv { color: #ffffff; font-family: var(--font-heading); font-weight: 500; font-size: 12px; line-height: 1; }
        .hp-wl { color: rgba(255,255,255,0.6); font-family: var(--font-body); font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; margin-top: 3px; }

        /* Dots */
        .hero-phone-dots {
          display: flex; justify-content: center; gap: 8px; margin-top: 18px;
        }
        .hero-phone-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: rgba(238,216,154,0.30); border: none; padding: 0; cursor: pointer;
          transition: all .3s ease;
        }
        .hero-phone-dot.active { background: #EED89A; box-shadow: 0 0 10px rgba(238,216,154,0.6); width: 20px; border-radius: 4px; }

        /* Sound toggle on the phone frame */
        .hero-phone-sound {
          position: absolute; top: 14px; right: 18px; z-index: 4;
          width: 26px; height: 26px; border-radius: 50%;
          background: rgba(15,30,68,0.85); border: 1px solid rgba(238,216,154,0.35);
          color: rgba(255,255,255,0.7); display: flex; align-items: center; justify-content: center;
          cursor: pointer; padding: 0; transition: all .3s ease;
          backdrop-filter: blur(4px);
        }
        .hero-phone-sound:hover { color: #EED89A; border-color: rgba(238,216,154,0.6); }
        .hero-phone-sound.on {
          background: rgba(238,216,154,0.18); border-color: rgba(238,216,154,0.7);
          color: #EED89A;
          box-shadow: 0 0 14px rgba(238,216,154,0.35);
          animation: soundPulse 2.5s ease-in-out infinite;
        }
        @keyframes soundPulse {
          0%,100% { box-shadow: 0 0 14px rgba(238,216,154,0.35); }
          50% { box-shadow: 0 0 22px rgba(238,216,154,0.55); }
        }
        @media (prefers-reduced-motion: reduce) { .hero-phone-sound.on { animation: none; } }

      `}</style>

      {/* Floating gold motes */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden" aria-hidden>
        <span className="lwc-mote" style={{ left:"6%", animationDuration:"18s", animationDelay:"0s" }} />
        <span className="lwc-mote" style={{ left:"16%", animationDuration:"22s", animationDelay:"3s" }} />
        <span className="lwc-mote" style={{ left:"28%", animationDuration:"25s", animationDelay:"6s" }} />
        <span className="lwc-mote large" style={{ left:"38%", animationDuration:"28s", animationDelay:"9s" }} />
        <span className="lwc-mote" style={{ left:"48%", animationDuration:"20s", animationDelay:"2s" }} />
        <span className="lwc-mote" style={{ left:"58%", animationDuration:"24s", animationDelay:"5s" }} />
        <span className="lwc-mote large" style={{ left:"68%", animationDuration:"26s", animationDelay:"11s" }} />
        <span className="lwc-mote" style={{ left:"78%", animationDuration:"23s", animationDelay:"4s" }} />
        <span className="lwc-mote" style={{ left:"88%", animationDuration:"27s", animationDelay:"8s" }} />
        <span className="lwc-mote" style={{ left:"94%", animationDuration:"21s", animationDelay:"1s" }} />
      </div>

      {/* HEADER */}
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/5" style={{ background: "rgba(20,34,64,0.6)" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => { sessionStorage.removeItem("mmg_lwc_access"); window.location.assign("/living-with-cancer-access"); }}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition text-sm"
            data-testid="lwc-exit-portal"
          >
            <ArrowLeft size={16} /> Exit Portal
          </button>
          <Logo size="md" />
          <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white/70">
            <Sparkles size={12} className="text-[#EED89A]" /> Concept Preview
          </div>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="relative pt-20 pb-24 sm:pt-28 sm:pb-32 px-6 sm:px-10">
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-16 items-center">
          <div className="reveal is-visible">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-soft text-[11px] uppercase tracking-[0.28em] text-white/80 mb-4">
              <HeartPulse size={12} className="text-[#EED89A]" /> A concept for people living with and beyond cancer
            </div>
            <p className="font-body text-[11px] uppercase tracking-[0.24em] text-[#EED89A]/85 mb-8">
              Private concept materials · Independent concept draft
            </p>
            <h1 className="lwc-heading font-heading font-medium text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-balance">
              Care for the life <span className="italic">around</span> the treatment.
            </h1>
            <p className="mt-6 max-w-2xl font-body text-lg sm:text-xl text-white/85 leading-relaxed text-balance">
              My Mood Groove is a private iPhone app for emotional awareness, designed to complement
              cancer care, survivorship, research, and compassionate navigation, and to grow with a
              survivor's awareness over time. Not a diagnosis. Not psychotherapy. A quiet, private
              companion between appointments, and long after.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 text-[13px] text-white/70">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-soft"><Shield size={12} className="text-[#EED89A]" /> Privacy before insight</span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-soft"><Feather size={12} className="text-[#EED89A]" /> Person-centered, not cancer-centered</span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-soft"><Clock size={12} className="text-[#EED89A]" /> Longitudinal, over time</span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-soft"><Fingerprint size={12} className="text-[#EED89A]" /> Governed by patient reviewers</span>
            </div>
          </div>
          <div className="reveal is-visible relative flex justify-center lg:justify-end" aria-hidden="true">
            <HeroPhone />
          </div>
        </div>
      </section>

      {/* 2. WEARABLE BRIDGE, THE WEDGE */}
      <section className="relative py-20 px-6 sm:px-10 paper-band">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal grid lg:grid-cols-[1fr_0.85fr] gap-8 lg:gap-12 items-center mb-12">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">Wearable body–mind bridge · The wedge</div>
              <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
                Cancer survivors are already wearing the hardware.
              </h2>
              <p className="mt-5 font-body text-white/80 leading-relaxed">
                Wearable integration is not speculative. It is optional, private, and never diagnostic. A way for the
                experience surrounding a signal to be understood, and for a useful response to be chosen.
              </p>
            </div>
            <div className="relative h-[260px] sm:h-[320px]" aria-hidden="true">
              <img
                src="/wearable-watch.png"
                alt="Smartwatch showing a heart-rate waveform"
                className="lwc-wear lwc-wear-a absolute right-2 top-1 w-[52%] select-none pointer-events-none"
              />
              <img
                src="/wearable-oura.png"
                alt="Silver smart ring"
                className="lwc-wear lwc-wear-b absolute left-0 bottom-0 w-[46%] select-none pointer-events-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {wearableStats.map(({ value, note, source }, i) => (
              <div key={value} className="reveal glass-card rounded-2xl p-6 sm:p-7 text-center" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`lwc-stat-${i}`}>
                <div className="text-[42px] sm:text-[48px] font-heading font-medium text-[#EED89A] leading-none tracking-tight mb-3">{value}</div>
                <div className="text-white/85 text-[14px] leading-relaxed mb-3">{note}</div>
                <div className="text-white/50 text-[11px] uppercase tracking-[0.14em]">{source}</div>
              </div>
            ))}
          </div>
          <div className="reveal mt-8 glass-card rounded-2xl p-6 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-2">Design principle</div>
                <p className="text-white/85 text-[14.5px] leading-relaxed">
                  People without wearables must receive a complete experience, not a reduced one.
                  Device lending, technical support, and simple onboarding prevent favoring younger or higher-income survivors.
                </p>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-2">Research potential</div>
                <p className="text-white/85 text-[14.5px] leading-relaxed">
                  A study comparing conversation-only, wearable-only, and combined body–mind experiences could
                  shape a new category of responsible survivorship support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. OUR VISION FOR IMPACT */}
      <section className="relative py-20 px-6 sm:px-10 paper-band">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">Our vision for impact</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              From clinical care to the lived experience, and everything in between.
            </h2>
            <p className="mt-5 font-body text-white/80 leading-relaxed">
              My Mood Groove is designed to complement, never replace, cancer organizations, hospitals,
              survivorship programs, research institutions, and community partners already dedicated to this work.
              It is the private layer between the appointments.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {visionPoints.map(({ icon: Icon, tag, title, body }, i) => (
              <div key={title} className="reveal glass-card rounded-2xl p-6 sm:p-7" style={{ transitionDelay: `${i * 100}ms` }} data-testid={`lwc-pillar-${i}`}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-9 h-9 rounded-xl bg-[#EED89A]/10 border border-[#EED89A]/40 flex items-center justify-center">
                    <Icon size={16} className="text-[#EED89A]" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A]">{tag}</span>
                </div>
                <div className="font-heading font-medium text-white text-xl leading-snug mb-2">{title}</div>
                <p className="font-body text-white/75 text-[14px] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. SUPPORT WINDOWS */}
      <section className="relative py-20 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">The opportunity</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              Cancer care is organized around clinical outcomes. People carry more than that.
            </h2>
            <p className="mt-5 font-body text-white/80 leading-relaxed">
              Uncertainty. Fear. Exhaustion. Body changes. Family responsibilities. Disrupted identity.
              Financial pressure. Societal expectations of strength. My Mood Groove is a voluntary, private
              place to pause, speak, regulate, reflect, prepare, and choose what happens next.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {supportWindows.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="reveal glass-card rounded-2xl p-6 sm:p-7" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`lwc-window-${i}`}>
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-white/[0.05] border border-[#EED89A]/35 flex items-center justify-center">
                    <Icon size={20} className="text-[#EED89A]" />
                  </span>
                  <div>
                    <div className="font-heading text-white text-[16px] leading-tight mb-2">{title}</div>
                    <p className="font-body text-white/75 text-[14px] leading-relaxed">{body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="reveal mt-10 max-w-2xl font-body text-white/70 italic leading-relaxed">
            "The platform would not ask people to be positive. It would help them be honest, steady, informed, and supported."
          </p>
        </div>
      </section>

      {/* 4. WHAT THIS IS / WHAT THIS ISN'T */}
      <section className="relative py-20 px-6 sm:px-10 ivory-band">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#132A5C] mb-3">Guardrails</div>
            <h2 className="lwc-heading ivory font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight">
              What this is. What this isn't.
            </h2>
            <p className="mt-5 font-body text-[#3d4a66] leading-relaxed">
              Clarity of scope protects the person, the clinician, and the strategic partner.
            </p>
          </div>
          <div className="reveal rounded-2xl border border-[#c9d3e6] bg-white/70 backdrop-blur overflow-hidden shadow-[0_20px_60px_rgba(20,40,80,0.08)]">
            <div className="grid grid-cols-2 divide-x divide-[#dfe4ee]">
              <div className="p-5 text-[11px] uppercase tracking-[0.24em] text-[#132A5C] font-medium bg-[#D0DEEC]/55">What My Mood Groove does</div>
              <div className="p-5 text-[11px] uppercase tracking-[0.24em] text-[#132A5C] font-medium bg-[#D0DEEC]/55">What it does not do</div>
            </div>
            {contrastRows.map(([a, b], i) => (
              <div key={i} className="grid grid-cols-2 divide-x divide-[#e4e8f0] border-t border-[#e4e8f0]">
                <div className="p-5 text-[#132A5C] font-body text-[14px] leading-relaxed">{a}</div>
                <div className="p-5 text-[#4c5a7d] font-body text-[14px] leading-relaxed italic">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. THE WOMAN'S EXPERIENCE */}
      <section className="relative py-20 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">The lived experience</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              A private moment between appointments, treatments, and ordinary life.
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-px bg-gradient-to-b from-[#EED89A]/40 via-white/15 to-transparent" />
            <div className="space-y-6">
              {journeySteps.map(({ n, title, body }, i) => (
                <div key={n} className="reveal relative pl-14 sm:pl-20" style={{ transitionDelay: `${i * 70}ms` }} data-testid={`lwc-journey-${i}`}>
                  <div className="absolute left-0 top-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#EED89A] text-[#22314E] font-heading font-semibold flex items-center justify-center shadow-[0_0_0_4px_rgba(238,216,154,0.15)]">
                    {n}
                  </div>
                  <div className="glass-card rounded-2xl p-5 sm:p-6">
                    <div className="font-heading font-medium text-white text-lg mb-1.5">{title}</div>
                    <p className="font-body text-white/75 text-[14.5px] leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="reveal mt-10 max-w-2xl font-body text-white/60 italic text-[14px] leading-relaxed">
            The artificial intelligence is not the authority on any body, treatment, or future. It is a structured aid to awareness and choice.
          </p>
        </div>
      </section>

      {/* 6. PHONE MOCKUPS, Interactive tabs */}
      <section className="relative py-24 px-6 sm:px-10 paper-band">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12 text-center mx-auto">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">Six moments, one private companion</div>
            <h2 className="lwc-heading centered font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              What you see. What you choose.
            </h2>
            <p className="mt-5 font-body text-white/80 leading-relaxed">
              A glimpse of how My Mood Groove holds a moment, without diagnosis, without pressure, without a score.
            </p>
          </div>

          {/* Tabs */}
          <div className="reveal flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10">
            {phoneScreens.map((s, i) => (
              <button
                key={s.id}
                onClick={() => selectScreen(i)}
                className={`screen-tab font-body ${activeScreen === i ? "active" : ""}`}
                data-testid={`lwc-screen-tab-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Phone + description */}
          <div className="reveal grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="flex justify-center">
              <PhoneFrame testid={`lwc-phone-${phoneScreens[activeScreen].id}`} floatVariant={activeScreen % 2 === 0 ? "a" : "b"}>
                {phoneScreens[activeScreen].body}
              </PhoneFrame>
            </div>
            <div className="max-w-md">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">Screen {activeScreen + 1} of {phoneScreens.length}</div>
              <div className="font-heading font-medium text-white text-3xl mb-4 leading-tight">{phoneScreens[activeScreen].label}</div>
              <p className="font-body text-white/80 text-[15px] leading-relaxed">
                {activeScreen === 0 && "The person arrives without predefined labels. The first screen does not ask for a name, a score, or a mood. It offers presence, and a doorway."}
                {activeScreen === 1 && "A short list of intentions, not diagnoses. They may choose one, several, or none, and the experience adapts to what they actually need."}
                {activeScreen === 2 && "Voice, text, or silence. A library of meditation sounds like warm piano, ocean, rain, forest, chants, or silence. And a set of calming visual landscapes: aurora, forest, tide, meadow, coastline. Any landscape can be cast to a laptop, TV, or projector for a full-room calming environment. Nothing is transcribed unless kept."}
                {activeScreen === 3 && "For fear of recurrence, the platform helps distinguish a thought from a medical fact. It never predicts. It never reassures falsely."}
                {activeScreen === 4 && "One realistic next step, not a list of tasks. Rest counts. A prepared question counts. Urgent care is always visible and never buried."}
                {activeScreen === 5 && "If they choose to connect a wearable, subjective and objective sit alongside each other. Nothing is scored. Nothing is judged."}
              </p>
              <div className="mt-6 flex flex-wrap gap-2 text-[11px] text-white/70">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass-soft"><Shield size={11} className="text-[#EED89A]" /> Private</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass-soft"><Feather size={11} className="text-[#EED89A]" /> Accessible</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass-soft"><CircleUser size={11} className="text-[#EED89A]" /> Person-controlled</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. PSYCHOLOGICAL FOUNDATIONS */}
      <section className="relative py-20 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">The evidence base</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              Grounded in psychology, not opinion.
            </h2>
            <p className="mt-5 font-body text-white/80 leading-relaxed">
              Every doctrine traces back to established practice in psycho-oncology, behavioral science,
              and evidence-based mental health care. My Mood Groove is not an alternative to therapy.
              It is an app-scaled application of frameworks clinicians have used for decades to help
              people move through stress, anxiety, depression, grief, and uncertainty with dignity.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {psychologyFrameworks.map(({ tag, title, body, shapes }, i) => (
              <div key={title} className="reveal glass-card rounded-2xl p-6 sm:p-7 flex flex-col" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`lwc-psych-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A] mb-2">{tag}</div>
                <div className="font-heading font-medium text-white text-[17px] leading-snug mb-3">{title}</div>
                <p className="font-body text-white/75 text-[13.5px] leading-relaxed mb-4">{body}</p>
                <div className="mt-auto pt-4 border-t border-white/8">
                  <div className="text-[9px] uppercase tracking-[0.22em] text-white/55 mb-1.5">Shapes</div>
                  <p className="font-body text-[#EED89A]/95 text-[12.5px] leading-relaxed italic">{shapes}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="reveal mt-10 max-w-2xl font-body text-white/60 italic text-[14px] leading-relaxed">
            Where the evidence is unsettled, the platform sits with the uncertainty. Where a moment
            calls for a human clinician, the platform steps aside and points the way.
          </p>
        </div>
      </section>

      {/* 8. 16 DOCTRINES */}
      <section className="relative py-20 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">Behavioral doctrine system</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              Sixteen doctrines for people facing cancer.
            </h2>
            <p className="mt-5 font-body text-white/80 leading-relaxed">
              The ethical and functional backbone. Written to be reviewed by patients, survivors, physicians,
              nurses, psycho-oncology professionals, patient navigators, and accessibility experts.
            </p>
          </div>
          <div className="space-y-8">
            {doctrineGroups.map((g, gi) => (
              <div key={g.label} className="reveal" style={{ transitionDelay: `${gi * 80}ms` }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-px w-10 bg-gradient-to-r ${g.accent} to-transparent`} />
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A]">{g.label}</div>
                  <div className={`flex-1 h-px bg-gradient-to-r ${g.accent} to-transparent`} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {g.items.map((d) => (
                    <div key={d.n} className="glass-card rounded-2xl p-5 sm:p-6" data-testid={`lwc-doctrine-${d.n}`}>
                      <div className="flex items-baseline gap-3 mb-2">
                        <span className="text-[#EED89A] font-heading font-semibold text-lg">{d.n}.</span>
                        <div className="font-heading font-medium text-white text-[15.5px] leading-snug">{d.title}</div>
                      </div>
                      <p className="font-body text-white/75 text-[13.5px] leading-relaxed pl-7">{d.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. DEEP DIVES, Fear, Body, Connection */}
      <section className="relative py-20 px-6 sm:px-10 paper-band">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">Deep-dive doctrines</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              Where responsibility meets tenderness.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {deepDives.map(({ icon: Icon, title, may, mustNot, coda }, i) => (
              <div key={title} className="reveal glass-card rounded-2xl p-6 sm:p-7 flex flex-col" style={{ transitionDelay: `${i * 100}ms` }} data-testid={`lwc-deepdive-${i}`}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-9 h-9 rounded-xl bg-[#EED89A]/10 border border-[#EED89A]/40 flex items-center justify-center">
                    <Icon size={16} className="text-[#EED89A]" />
                  </span>
                </div>
                <div className="font-heading font-medium text-white text-lg leading-snug mb-4">{title}</div>
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A] mb-2">What it may do</div>
                  <ul className="space-y-1.5">
                    {may.map((x) => <li key={x} className="text-white/80 text-[13px] leading-relaxed pl-4 relative before:content-['·'] before:absolute before:left-0 before:text-[#EED89A]">{x}</li>)}
                  </ul>
                </div>
                <div className="mb-5">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-white/60 mb-2">What it must not do</div>
                  <ul className="space-y-1.5">
                    {mustNot.map((x) => <li key={x} className="text-white/60 text-[13px] leading-relaxed italic pl-4 relative before:content-['×'] before:absolute before:left-0 before:text-white/40">{x}</li>)}
                  </ul>
                </div>
                <div className="gold-divider mb-4" />
                <p className="font-body text-white/85 text-[13.5px] leading-relaxed italic mt-auto">{coda}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. ACCESSIBILITY + TRUST ARCHITECTURE */}
      <section className="relative py-20 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="reveal glass-card rounded-3xl p-8 sm:p-10">
            <div className="flex items-center gap-2 mb-4">
              <Feather size={16} className="text-[#EED89A]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A]">Accessible experience</span>
            </div>
            <div className="font-heading font-medium text-white text-2xl leading-snug mb-4">Designed for fluctuating energy and cognitive capacity.</div>
            <ul className="space-y-2.5 text-white/80 text-[14px] leading-relaxed">
              <li>Simple screens, large readable text, and low-effort navigation.</li>
              <li>Voice, typing, and one-tap check-ins for days when concentration is limited.</li>
              <li>Sensitive to headaches, sensory overload, neuropathy, cognitive fog, and pain.</li>
              <li>Plain language. Never presented as though the platform has interpreted a person's condition.</li>
              <li>Tested with people across age, language, disability, treatment stage, and culture.</li>
            </ul>
            <p className="mt-6 text-white/70 italic text-[13.5px] leading-relaxed">
              Accessibility is not a convenience feature. It determines who can actually receive support.
            </p>
          </div>
          <div className="reveal glass-card rounded-3xl p-8 sm:p-10">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={16} className="text-[#EED89A]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A]">Trust architecture</span>
            </div>
            <div className="font-heading font-medium text-white text-2xl leading-snug mb-4">Privacy must survive institutional and philanthropic interest.</div>
            <div className="space-y-4 text-[14px] leading-relaxed">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A] mb-1.5">Individual layer</div>
                <p className="text-white/80">Private conversations, private biometric context, user-controlled memory, clear deletion, transparent data use, no hidden monitoring.</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A] mb-1.5">Prohibited uses (by any entity)</div>
                <p className="text-white/80">Insurance decisions. Employment decisions. Treatment eligibility. Donor storytelling without consent. Individual distress scoring. Any pressure to share private content.</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A] mb-1.5">Partner layer</div>
                <p className="text-white/80">Only consented, deidentified, threshold-protected aggregate insights. No ability to identify a person or reconstruct their narrative.</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#EED89A] mb-1.5">Governance</div>
                <p className="text-white/80">Defined access roles, purpose limitation, retention rules, audit logs, cybersecurity review, ethics oversight, patient representation, independent evaluation.</p>
              </div>
            </div>
            <p className="mt-6 text-white/70 italic text-[13.5px] leading-relaxed">
              A strategic partner should help protect the person's private voice, not become another audience for it.
            </p>
          </div>
        </div>
      </section>

      {/* (Wearable Bridge moved earlier, see section 2 above) */}

      {/* 11. PILOT PATHWAY */}
      <section className="relative py-20 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">A responsible first pilot</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              Six phases. Reviewed by patients and experts, at every step.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pilotPhases.map(({ n, title, body }, i) => (
              <div key={n} className="reveal glass-card rounded-2xl p-6 sm:p-7" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`lwc-phase-${i}`}>
                <div className="text-[11px] uppercase tracking-[0.24em] text-[#EED89A] mb-2">{n}</div>
                <div className="font-heading font-medium text-white text-lg leading-snug mb-2">{title}</div>
                <p className="font-body text-white/75 text-[14px] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 12. POTENTIAL ROLES FOR STRATEGIC PARTNERS */}
      <section className="relative py-20 px-6 sm:px-10 paper-band">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-3">Potential roles for strategic partners</div>
            <h2 className="lwc-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              Partners need not operate the technology to shape the standard.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {partnerRoles.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="reveal glass-card rounded-2xl p-6 sm:p-7" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`lwc-role-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl bg-[#EED89A]/12 border border-[#EED89A]/45 flex items-center justify-center">
                    <Icon size={18} className="text-[#EED89A]" />
                  </span>
                  <div className="font-heading font-medium text-white text-[15.5px]">{title}</div>
                </div>
                <p className="font-body text-white/75 text-[13.5px] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 13. CLOSING */}
      <section className="relative py-28 px-6 sm:px-10">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="reveal">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#EED89A] mb-4">Closing proposition</div>
            <h2 className="lwc-heading centered font-heading font-medium text-4xl sm:text-6xl tracking-tight leading-[1.05] text-balance">
              Support the person.<br />Strengthen the care.<br /><span className="italic">Expand what survivorship can mean.</span>
            </h2>
            <p className="mt-8 max-w-2xl mx-auto font-body text-white/85 text-lg leading-relaxed text-balance">
              People facing cancer deserve more than medical information and encouragement to remain strong.
              They deserve a private place where fear, fatigue, grief, hope, anger, uncertainty, body changes,
              family pressure, and ordinary life can be held without judgment.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-[13px] text-white/70">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-soft"><HeartPulse size={12} className="text-[#EED89A]" /> Person-centered</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-soft"><Shield size={12} className="text-[#EED89A]" /> Privacy first</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-soft"><BookOpen size={12} className="text-[#EED89A]" /> Research-ready</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-soft"><Palette size={12} className="text-[#EED89A]" /> Culturally adaptable</span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative border-t border-white/10 mt-8">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 text-center">
          <p className="font-body text-[11px] uppercase tracking-[0.22em] text-white/50 mb-3">
            Independent concept draft
          </p>
          <p className="font-body text-[13px] text-white/60 max-w-3xl mx-auto leading-relaxed">
            Independent concept draft. Not affiliated with, endorsed by, or presented on behalf of any specific
            foundation, hospital, or research institution. Access does not imply endorsement, partnership, or
            funding commitment.
          </p>
          <button
            type="button"
            onClick={() => { sessionStorage.removeItem("mmg_lwc_access"); window.location.assign("/living-with-cancer-access"); }}
            className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-white/15 text-white/85 hover:bg-white/[0.05] transition text-sm"
            data-testid="lwc-footer-exit"
          >
            <ArrowLeft size={14} /> Exit Portal
          </button>
        </div>
      </footer>
    </div>
  );
}
